import { updateCustomer } from 'ducks/customers/actions'
import {
  doActivateCardTab,
  doUpdateCardLoading,
  doUpdateCardCollapsing,
  doUpdateCardHidden,
  doContextChangeRegistered,
  doFetchEmbeddableCardContents,
  doUpdateCardTabTitle,
  triggerEmbeddableCardErrored,
  triggerEmbeddableCardSuccess,
} from 'actions/embeddable_cards'
import {
  selectContextBuilder,
  selectContextCacheKeyBuilder,
} from 'selectors/embeddable_cards'

const GrooveCards = {
  triggerChangeCallback(message) {
    Object.values(GrooveCards.cards).forEach(card => {
      if (
        card.triggerChangeCallback &&
        typeof card.triggerChangeCallback === 'function'
      ) {
        Object.keys(card.frames).forEach(location => {
          card.triggerChangeCallback(
            card.frames[location],
            card.getContext(location),
            message
          )
        })
      }
    })
  },
  forCard(key) {
    const existingCard = GrooveCards.cards[key]
    if (existingCard) return existingCard

    const publicCard = {}
    GrooveCards.public.cards[key] = publicCard
    const card = {}
    GrooveCards.cards[key] = card

    // Public API
    Object.assign(publicCard, {
      key,
      loading(arg1 = true, arg2 = true) {
        let isLoading
        let cacheKey
        if (arguments.length < 2) {
          isLoading = arg1
          cacheKey = 'global'
        } else {
          isLoading = arg2
          cacheKey = arg1
        }
        app.store.dispatch(doUpdateCardLoading(key, cacheKey, !!isLoading))
      },
      isLoading(cacheKey) {
        const content = card.getContentForCacheKey(cacheKey)
        const state = card.getState()
        const contentLoading = !content || content.fetching
        const hasCallback = card.contextChangeCallback
        const isBusy = state.isBusy[cacheKey] // || state.isBusy.global
        return (contentLoading && !hasCallback) || isBusy
      },
      hide(hidden = true) {
        app.store.dispatch(doUpdateCardHidden(key, !!hidden))
      },
      show(shown = true) {
        card.hide(!shown)
      },
      isHidden() {
        const state = card.getState()
        return state.isHidden
      },
      reload() {
        app.store.dispatch(doFetchEmbeddableCardContents(card, false))
      },
      initialState(state) {
        if (state) card.initialStateValue = state
        return card.initialStateValue || {}
      },
      getSidebarElement() {
        return document.querySelector(
          `#EmbeddableCard-${key} .EmbeddableCardHook`
        )
      },
      getContext(location) {
        return selectContextBuilder(app.store.getState())(location)
      },
      getCacheKey(location) {
        return selectContextCacheKeyBuilder(app.store.getState())(location)
      },
      onContextChange(callback) {
        card.contextChangeCallback = callback
        app.store.dispatch(doContextChangeRegistered(card.key))
      },
      onChange(callback) {
        card.changeCallback = callback
      },
      hasTab(title) {
        app.store.dispatch(doUpdateCardTabTitle(card.key, title))
      },
      activateTab(keyOverride) {
        app.store.dispatch(doActivateCardTab(keyOverride || card.key))
      },
      updateCustomer: (customerId, params) => {
        app.store.dispatch(updateCustomer(customerId, params))
      },
    })

    // Private API, - public API is merged and also available on this object
    return Object.assign(card, publicCard, {
      public: publicCard,
      frames: {},
      isCollapsed(cacheKey) {
        const state = card.getState()
        const isCollapsed =
          state.isCollapsed && state.isCollapsed[cacheKey] === true
        return isCollapsed
      },
      toggleCollapsing(cacheKey) {
        const isCollapsed = card.isCollapsed(cacheKey)
        app.store.dispatch(doUpdateCardCollapsing(key, cacheKey, !isCollapsed))
      },
      triggerChangeCallback(frame, context, event) {
        card.triggerAndCatch(frame, context, card.changeCallback, event)
      },
      triggerContextChangeCallback(frame, context) {
        card.triggerAndCatch(frame, context, card.contextChangeCallback)
      },
      triggerAndCatch(frame, context, callback, event) {
        if (!callback) return
        if (!context) return
        try {
          callback(frame, context, event)
          app.store.dispatch(
            triggerEmbeddableCardSuccess(
              card.key,
              context.id,
              card.getCacheKey()
            )
          )
        } catch (e) {
          app.store.dispatch(
            triggerEmbeddableCardErrored(
              card.key,
              context.id,
              card.getCacheKey(),
              e.message
            )
          )
          // eslint-disable-next-line no-console
          console.error(e)
        }
      },
      getState() {
        const cardState = app.store.getState().embeddableCards
        const { byKey } = cardState
        return byKey[card.key]
      },
      getContentForCacheKey(cacheKey) {
        const contents = card.getState().content
        const content = contents && contents[cacheKey]
        return content && content
      },
      isContentLoaded(cacheKey) {
        const content = card.getContentForCacheKey(cacheKey)
        return !!(content && content.content)
      },
      hasErrored(cacheKey) {
        const content = card.getContentForCacheKey(cacheKey)
        return !!(content && content.errored)
      },
      renderContentIntoFrame(frame, context, cacheKey) {
        const cardFrame = frame
        if (!cardFrame) return
        const content = card.getContentForCacheKey(cacheKey)
        if (!card.isContentLoaded(cacheKey)) return

        cardFrame.innerHTML = content.content
        const scripts = Array.from(frame.querySelectorAll('script'))
        scripts.forEach(script => {
          const scriptTag = script
          scriptTag.originalPreviousSibling = script.previousSibling
          scriptTag.originalParentElement = script.parentElement
          scriptTag.remove()
        })

        scripts.forEach(script => {
          const newScript = document.createElement('script')
          Array.from(script.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value)
          })
          newScript.text = script.text

          const originalOnError = window.onerror
          window.onerror = e => {
            app.store.dispatch(
              triggerEmbeddableCardErrored(
                card.key,
                context.id,
                card.getCacheKey(),
                e.message
              )
            )
            // eslint-disable-next-line no-console
            console.error(e)
          }
          script.originalParentElement.insertBefore(
            newScript,
            script.originalPreviousSibling
          )
          window.onerror = originalOnError
        })
      },
    })
  },

  public: {
    forCard(key) {
      return GrooveCards.forCard(key).public
    },
    cards: {},
  },

  cards: {},
  outerFrames: {},
}

export default GrooveCards
global.GrooveCards = GrooveCards.public
